import { MazeState, PrintConfig, WallSegment, PathSegment } from '../types/maze-types';
import * as QRCode from 'qrcode';
import { MAZE_COLORS, GAME_COLORS } from '@/contexts/games/constants/colors';
import { MAZE_EMOJIS } from '../config/mazeEmojis';
import { logger } from '@/shared/infrastructure/logging/logger';

/**
 * Paper dimensions in mm
 */
const PAPER_SIZES = {
  A4: { width: 210, height: 297 },
  Letter: { width: 215.9, height: 279.4 },
  A3: { width: 297, height: 420 }
} as const;

/**
 * MazePrinter - Handles print/download functionality
 * Converts canvas maze to SVG for vector output
 */
export class MazePrinter {
  /**
   * Get paper dimensions based on size and orientation
   */
  private static getPaperDimensions(size: 'A4' | 'Letter' | 'A3', orientation: 'portrait' | 'landscape') {
    const dim = PAPER_SIZES[size];
    return orientation === 'landscape'
      ? { width: dim.height, height: dim.width }
      : dim;
  }

  /**
   * Calculate scale factor to fit maze on paper with margins
   */
  private static calculateScale(state: MazeState, dimensions: { width: number; height: number }, margin: number): number {
    const availableWidth = dimensions.width - 2 * margin;
    const availableHeight = dimensions.height - 2 * margin;

    const mazeWidth = state.cols * state.cellSize;
    const mazeHeight = state.rows * state.cellSize;

    const scaleX = availableWidth / mazeWidth;
    const scaleY = availableHeight / mazeHeight;

    return Math.min(scaleX, scaleY);
  }

  /**
   * Convert wall segment to SVG path command
   */
  private static wallSegmentToSVGPath(wall: WallSegment): string {
    const { start, end, control1, type, radius } = wall;

    if (type === 'line') {
      return `M ${start.x},${start.y} L ${end.x},${end.y}`;
    } else if (type === 'bezier' && control1) {
      return `M ${start.x},${start.y} Q ${control1.x},${control1.y} ${end.x},${end.y}`;
    } else if (type === 'arc' && control1 && radius) {
      return `M ${start.x},${start.y} A ${radius},${radius} 0 0 1 ${end.x},${end.y}`;
    }

    return '';
  }

  /**
   * Convert path segment to SVG path command
   */
  private static pathSegmentToSVGPath(segment: PathSegment): string {
    if (segment.points.length < 2) return '';

    let path = `M ${segment.points[0].x},${segment.points[0].y}`;
    for (let i = 1; i < segment.points.length; i++) {
      path += ` L ${segment.points[i].x},${segment.points[i].y}`;
    }

    return path;
  }

  /**
   * Generate QR code as SVG path
   */
  private static async generateQRCodeSVG(url: string, size: number): Promise<string> {
    try {
      // Generate QR code as data URL
      const qrDataUrl = await QRCode.toDataURL(url, {
        errorCorrectionLevel: 'M',
        margin: 1,
        width: size,
        color: {
          dark: GAME_COLORS.grayDark,
          light: GAME_COLORS.white
        }
      });

      return qrDataUrl;
    } catch (error) {
      logger.error('Error generating QR code:', error);
      return '';
    }
  }

  /**
   * Convert canvas maze to print-optimized SVG
   */
  static async canvasToSVG(state: MazeState, config: PrintConfig, currentUrl?: string): Promise<string> {
    const dimensions = this.getPaperDimensions(config.paperSize, config.orientation);
    const scale = this.calculateScale(state, dimensions, config.margin);

    const mazeWidth = state.cols * state.cellSize * scale;
    const mazeHeight = state.rows * state.cellSize * scale;

    // Center maze on page
    const offsetX = (dimensions.width - mazeWidth) / 2;
    const offsetY = (dimensions.height - mazeHeight) / 2;

    const svgParts: string[] = [];

    // SVG header
    svgParts.push(`<?xml version="1.0" encoding="UTF-8"?>`);
    svgParts.push(`<svg xmlns="http://www.w3.org/2000/svg" width="${dimensions.width}mm" height="${dimensions.height}mm" viewBox="0 0 ${dimensions.width} ${dimensions.height}">`);

    // Background
    svgParts.push(`<rect width="${dimensions.width}" height="${dimensions.height}" fill="white"/>`);

    // Group for maze with transformation
    svgParts.push(`<g transform="translate(${offsetX}, ${offsetY}) scale(${scale})">`);

    // Draw zones if requested
    if (config.includeZones) {
      if (state.entranceZone) {
        const zone = state.entranceZone;
        const x = zone.topLeft.x * state.cellSize;
        const y = zone.topLeft.y * state.cellSize;
        const width = zone.size * state.cellSize;
        const height = zone.size * state.cellSize;

        svgParts.push(`<text x="${x + width / 2}" y="${y + height / 2}" text-anchor="middle" dominant-baseline="middle" font-size="${width * 0.45}" font-family="Arial">${MAZE_EMOJIS.entrance}</text>`);
      }

      if (state.exitZone) {
        const zone = state.exitZone;
        const x = zone.topLeft.x * state.cellSize;
        const y = zone.topLeft.y * state.cellSize;
        const width = zone.size * state.cellSize;

        svgParts.push(`<text x="${x + width / 2}" y="${y + width / 2}" text-anchor="middle" dominant-baseline="middle" font-size="${width * 0.45}" font-family="Arial">${MAZE_EMOJIS.exit}</text>`);
      }
    }

    // Draw walls
    if (state.wallSegments && state.wallSegments.length > 0) {
      // Render curved walls
      const wallPaths = state.wallSegments.map(wall => this.wallSegmentToSVGPath(wall)).filter(p => p);
      svgParts.push(`<g stroke="${GAME_COLORS.grayDark}" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round">`);
      wallPaths.forEach(path => {
        svgParts.push(`<path d="${path}"/>`);
      });
      svgParts.push(`</g>`);
    } else {
      // Render straight walls (legacy)
      svgParts.push(`<g stroke="${GAME_COLORS.grayDark}" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round">`);
      for (let y = 0; y < state.rows; y++) {
        for (let x = 0; x < state.cols; x++) {
          const cell = state.maze[y][x];
          const cx = x * state.cellSize;
          const cy = y * state.cellSize;

          if (cell.walls.top) {
            svgParts.push(`<path d="M ${cx},${cy} L ${cx + state.cellSize},${cy}"/>`);
          }
          if (cell.walls.right) {
            svgParts.push(`<path d="M ${cx + state.cellSize},${cy} L ${cx + state.cellSize},${cy + state.cellSize}"/>`);
          }
          if (cell.walls.bottom) {
            svgParts.push(`<path d="M ${cx},${cy + state.cellSize} L ${cx + state.cellSize},${cy + state.cellSize}"/>`);
          }
          if (cell.walls.left) {
            svgParts.push(`<path d="M ${cx},${cy} L ${cx},${cy + state.cellSize}"/>`);
          }
        }
      }
      svgParts.push(`</g>`);
    }

    // Draw user path if requested
    if (config.includePath && state.pathSegments.length > 0) {
      svgParts.push(`<g stroke="${GAME_COLORS.blue}" stroke-width="3" fill="none" stroke-linecap="round" stroke-linejoin="round" opacity="0.6">`);
      state.pathSegments.forEach(segment => {
        const pathData = this.pathSegmentToSVGPath(segment);
        if (pathData) {
          svgParts.push(`<path d="${pathData}"/>`);
        }
      });
      svgParts.push(`</g>`);
    }

    svgParts.push(`</g>`); // Close transformation group

    // Add QR code and link if requested
    if (config.includeQRCode) {
      // Use current page URL if provided, otherwise fallback to configured URL
      const gameUrl = currentUrl ||
        (typeof window !== 'undefined' ? window.location.href :
         `${process.env.NEXT_PUBLIC_APP_URL || process.env.NEXTAUTH_URL || 'https://owlcat.club'}/maze`);

      // Generate QR code
      const qrSize = 20; // mm
      const qrX = dimensions.width - qrSize - config.margin;
      const qrY = dimensions.height - qrSize - config.margin - 10; // 10mm space for text

      // Generate QR code image
      const qrDataUrl = await this.generateQRCodeSVG(gameUrl, 200);

      if (qrDataUrl) {
        svgParts.push(`<image x="${qrX}" y="${qrY}" width="${qrSize}" height="${qrSize}" href="${qrDataUrl}"/>`);

        // Add text link below QR code, aligned to the right edge
        const textY = qrY + qrSize + 4;
        const textX = qrX + qrSize; // Right edge of QR code
        svgParts.push(`<text x="${textX}" y="${textY}" text-anchor="end" font-size="3" font-family="Arial" fill="#666666">${gameUrl}</text>`);
      }
    }

    svgParts.push(`</svg>`);

    return svgParts.join('\n');
  }

  /**
   * Trigger browser print dialog with SVG
   */
  static printMaze(svgString: string, paperSize: string): void {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      logger.error('Failed to open print window');
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Maze Print</title>
        <style>
          @media print {
            @page { size: ${paperSize}; margin: 0; }
            body { margin: 0; padding: 0; }
            svg { display: block; }
          }
          body { margin: 0; padding: 0; }
          svg { width: 100%; height: 100vh; }
        </style>
      </head>
      <body>${svgString}</body>
      </html>
    `);
    printWindow.document.close();

    // Wait for content to load before printing
    printWindow.onload = () => {
      printWindow.print();
    };
  }

  /**
   * Download SVG as file
   */
  static downloadAsSVG(svgString: string, filename: string = 'maze.svg'): void {
    const blob = new Blob([svgString], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Download as PNG with high resolution using canvas
   */
  static async downloadAsPNG(
    canvasRef: HTMLCanvasElement,
    filename: string = 'maze.png',
    scaleFactor: number = 3
  ): Promise<void> {
    // Create high-res version of current canvas
    const highResCanvas = document.createElement('canvas');
    const ctx = highResCanvas.getContext('2d');

    if (!ctx) {
      logger.error('Failed to get canvas context');
      return;
    }

    highResCanvas.width = canvasRef.width * scaleFactor;
    highResCanvas.height = canvasRef.height * scaleFactor;

    ctx.scale(scaleFactor, scaleFactor);
    ctx.drawImage(canvasRef, 0, 0);

    // Convert to blob and download
    return new Promise<void>((resolve) => {
      highResCanvas.toBlob((blob) => {
        if (!blob) {
          logger.error('Failed to create blob');
          resolve();
          return;
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        resolve();
      }, 'image/png');
    });
  }
}
